import { useRef, useState } from "react";
import axios from "axios";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";
import { getAuthToken, API_BASE, IS_MOCK_BACKEND, toBackendUrl } from "@/lib/api";
import { toast } from "sonner";

const MOCK_UPLOAD_MAX_BYTES = 900 * 1024;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

/**
 * ImageUpload — drag/drop or click file picker that uploads to /api/admin/upload
 * and returns the served URL (relative path or absolute) via onChange.
 *
 * Props:
 *   value: current image URL (string)
 *   onChange: (newUrl: string) => void
 *   label?: optional label text
 *   testId?: optional data-testid prefix
 */
export default function ImageUpload({ value, onChange, label = "Image", testId = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file (jpg, png, webp).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large. Max 5 MB.");
      return;
    }
    if (IS_MOCK_BACKEND && file.size > MOCK_UPLOAD_MAX_BYTES) {
      toast.error("For cPanel mock hosting, keep images under 900 KB or use a direct image URL.");
      return;
    }
    setUploading(true);
    try {
      if (IS_MOCK_BACKEND) {
        const dataUrl = await fileToDataUrl(file);
        onChange(dataUrl);
        toast.success("Image saved for mock hosting");
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      const token = getAuthToken();
      const { data } = await axios.post(`${API_BASE}/admin/upload`, formData, {
        withCredentials: true,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      onChange(toBackendUrl(data.url));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const handleManualUrl = (e) => {
    onChange(e.target.value);
  };

  const remove = () => onChange("");

  return (
    <div data-testid={testId}>
      {label && <div className="text-sm font-medium mb-1.5">{label}</div>}
      {value ? (
        <div className="relative rsi-card overflow-hidden group">
          <img src={value} alt="" className="w-full h-48 object-cover" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="btn-primary !py-2 !px-3 text-xs"
              data-testid={`${testId}-replace`}
            >
              <Upload className="h-3.5 w-3.5" /> Replace
            </button>
            <button
              type="button"
              onClick={remove}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-white/90 text-red-600 hover:bg-white"
              data-testid={`${testId}-remove`}
            >
              <X className="h-3.5 w-3.5" /> Remove
            </button>
          </div>
          {uploading && (
            <div className="absolute inset-0 bg-white/85 grid place-items-center">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--rsi-purple)]" />
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`w-full border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
            dragging
              ? "border-[var(--rsi-purple)] bg-[var(--rsi-purple-tint)]"
              : "border-gray-300 bg-gray-50 hover:bg-gray-100"
          }`}
          data-testid={`${testId}-dropzone`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--rsi-purple)]" />
              <span className="text-sm">Uploading…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-gray-500">
              <div className="h-10 w-10 rounded-lg bg-white grid place-items-center">
                <ImageIcon className="h-5 w-5 text-[var(--rsi-purple)]" />
              </div>
              <div className="text-sm font-medium text-[var(--rsi-navy)]">Click or drag image here</div>
              <div className="text-xs text-gray-500">PNG, JPG, WebP · up to 5 MB</div>
            </div>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
      <div className="mt-2 text-[11px] text-gray-500">
        Or paste an image URL:{" "}
        <input
          type="text"
          value={value || ""}
          onChange={handleManualUrl}
          placeholder="https://…"
          className="text-[11px] font-mono-plex flex-1 px-2 py-1 border border-gray-200 rounded outline-none focus:border-[var(--rsi-purple)] w-64"
          data-testid={`${testId}-url-input`}
        />
      </div>
    </div>
  );
}
